import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import useMyMenu from 'hooks/useMyMenu';
import usePageAccess from 'hooks/usePageAccess';
import { grantedUrls } from 'menu-items/dynamicMenu';
import { getDefaultPathForRole } from './rolePath';

// ==============================|| ROLE GUARD ||============================== //

/** Does a granted route template cover this path? `:param` matches one segment. */
const covers = (template, path) => {
  const t = template.split('?')[0].split('/').filter(Boolean);
  const p = path.split('?')[0].split('/').filter(Boolean);
  if (!t.length || t.length > p.length) return false;
  return t.every((seg, i) => seg.startsWith(':') || seg === p[i]);
};

/**
 * Gate a route by role, OR by what the user's roles have actually been granted.
 *
 * `allowedRoles` is a hardcoded list naming the legacy personas, repeated across
 * ~90 routes. That alone made a role created in Role Definitions unusable: its code
 * appears in none of those lists, so every screen bounced it no matter what it had
 * been ticked for.
 *
 * Rather than rewrite all ~90 call sites, the guard now also accepts a user whose
 * granted routes cover the path being visited. The named roles keep working exactly
 * as before, and a new role works wherever its grants reach. Call sites can be
 * migrated off `allowedRoles` gradually.
 *
 * This is a NAVIGATION control, not the security boundary — the API enforces
 * permissions independently. A user who slips past this still cannot read anything
 * their role does not grant.
 */
export default function RoleGuard({ children, allowedRoles, allowedCodes = [], allowedVisibleCodes = [] }) {
  const { user, isLoggedIn, isInitialized } = useAuth();
  const myMenu = useMyMenu();
  const { canSeeCode } = usePageAccess();
  const navigate = useNavigate();
  const location = useLocation();

  const userRole = user?.role?.toLowerCase();
  // A role created from a template carries a PERSONA (menu_template) — the role it should
  // behave like. Honour it here so a role cloned from Therapist passes therapist-guarded
  // routes without every allowedRoles list naming the new code. Falls back to the role's
  // own code; existing persona roles have menu_template = their code, so this is a no-op
  // for them.
  const persona = (myMenu?.menu_template || user?.role)?.toLowerCase();
  const roleAllowed = useMemo(() => {
    const allowed = allowedRoles.map((r) => r.toLowerCase());
    return (Boolean(userRole) && allowed.includes(userRole)) || (Boolean(persona) && allowed.includes(persona));
  }, [userRole, persona, allowedRoles]);

  // Null myMenu means the grant lookup has not landed. Treat that as "not yet
  // known" and fall back to the role list alone, rather than bouncing someone off
  // a legitimate screen mid-load.
  const grantAllowed = useMemo(() => {
    if (!myMenu) return false;
    // A role carrying is_super_admin is granted EVERYTHING via the catch-all, so its
    // `sections` is the whole catalogue and cannot be used to widen access. Its REAL
    // grants live in `granted_menu` (built with the super-admin shortcut OFF), so honour
    // those instead — that is what lets a persona role reach a screen an admin ticked for
    // it in Role Definitions. TWO guards on that path: the admin console (/admin/*) stays
    // gated by role, so a clinical role ticked for an Administration feature still cannot
    // walk into it; and if granted_menu is absent (a stale pre-upgrade cache) fall back to
    // the role list alone rather than guess.
    if (myMenu.is_super_admin) {
      if (!myMenu.granted_menu) return false;
      // /admin/* is grant-gated, not blanket-closed: a persona role reaches an Administration
      // screen ONLY when it has actually been granted it in Role Definitions (granted_menu
      // covers the path). This is a NAVIGATION control — the API enforces independently — and
      // it is what lets an admin turn on, say, User Management for a supervisor and have it
      // both appear (see menu-items/index.jsx) and open. (Because these personas carry
      // is_super_admin, the backend PermissionChecker does not gate them granularly; tightening
      // that to real least-privilege is the separate Phase 2 work.)
      return grantedUrls({ sections: myMenu.granted_menu }).some((u) => covers(u, location.pathname));
    }
    return grantedUrls(myMenu).some((u) => covers(u, location.pathname));
  }, [myMenu, location.pathname]);

  // Grant by CATALOGUE CODE, not by sidebar coverage. `grantAllowed` above is built
  // from `sections`, which is the show_in_menu-filtered menu — so a granted page that
  // is reached from a card or a button rather than a sidebar item (Add New Child is the
  // case this exists for) is NOT in grantedUrls and would bounce despite being ticked in
  // Role Definitions. `granted_codes` is the REAL grant set the entry card itself gates
  // on (see pages/therapist/configuration.jsx), independent of show_in_menu and of the
  // is_super_admin catch-all, so matching on it opens the route to exactly the roles the
  // card is shown to. Same NAVIGATION-only caveat — the API enforces independently.
  const codeAllowed = useMemo(() => {
    if (!allowedCodes.length) return false;
    const granted = myMenu?.granted_codes;
    if (!Array.isArray(granted)) return false;
    return allowedCodes.some((c) => granted.includes(c));
  }, [myMenu, allowedCodes]);

  // Grant by VISIBILITY, matching the card/CTA that leads here. `codeAllowed` above needs a
  // POSITIVE row in granted_codes, but the entry cards for card-reached routes (e.g. "Onboard
  // New Child") gate on `canSeeCode` — which FAILS OPEN (shown unless explicitly denied for the
  // role). That mismatch bounced a role that could see and click the card but had no positive
  // grant for its code (a `bcba`/custom persona sharing the supervisor experience, or a code
  // like onboard_child that isn't even seeded so never appears in granted_codes). Gating the
  // route by the SAME canSeeCode check keeps the invariant simple: if you can click the card,
  // you can reach its destination. Navigation-only — the API still enforces data access.
  const visibleAllowed = useMemo(
    () => allowedVisibleCodes.length > 0 && allowedVisibleCodes.some((c) => canSeeCode(c)),
    [allowedVisibleCodes, canSeeCode]
  );

  const permitted = roleAllowed || grantAllowed || codeAllowed || visibleAllowed;

  useEffect(() => {
    // Wait for the auth context to finish bootstrapping before redirecting,
    // same reason as AuthGuard.
    if (!isInitialized) return;

    if (!isLoggedIn) {
      navigate('/login', { replace: true });
      return;
    }

    // Hold while the grant lookup is in flight: redirecting on a not-yet-known
    // answer would bounce a legitimately permitted user on every cold load.
    if (!roleAllowed && !myMenu) return;

    if (!permitted) {
      // TEMP DIAGNOSTIC — remove once the onboarding-bounce report is resolved. Logs the
      // exact reason a route denied, so we can see role/persona/flag ground truth instead
      // of guessing from the redirect target.
      // eslint-disable-next-line no-console
      console.warn('[RoleGuard] DENY', location.pathname, {
        userRole,
        persona,
        allowedRoles,
        allowedVisibleCodes,
        roleAllowed,
        grantAllowed,
        codeAllowed,
        visibleAllowed,
        granted_codes: myMenu?.granted_codes,
        is_super_admin: myMenu?.is_super_admin
      });
      // Send them to their own landing page. Shares getDefaultPathForRole with
      // AuthLogin and GuestGuard: when this had its own copy of the ladder, a
      // role added to one and not the other bounced between the two.
      navigate(getDefaultPathForRole(userRole, myMenu), { replace: true });
    }
  }, [isInitialized, isLoggedIn, permitted, roleAllowed, myMenu, userRole, navigate]);

  // While auth is still bootstrapping, render nothing so we don't briefly
  // flash a redirect or unauthorized state.
  if (!isInitialized) return null;
  if (!isLoggedIn) return null;
  // Same hold as the effect: render through until the grant answer arrives.
  if (!permitted && !roleAllowed && !myMenu) return null;
  if (!permitted) return null;

  return children;
}

RoleGuard.propTypes = {
  children: PropTypes.any,
  allowedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  // Optional: catalogue page/feature codes that also open the route when the user has
  // been granted them in Role Definitions, regardless of whether they surface a sidebar
  // item. Lets a route be gated by GRANT rather than by a hardcoded persona list.
  allowedCodes: PropTypes.arrayOf(PropTypes.string),
  // Optional: catalogue codes whose VISIBILITY (canSeeCode — fails open, mirrors the entry
  // card's own gate) opens the route. Use for card-reached routes so anyone who can see and
  // click the card can reach it, independent of role name or positive granted_codes.
  allowedVisibleCodes: PropTypes.arrayOf(PropTypes.string)
};
